import './style.css'

import ReactDOM from 'react-dom/client'
import { Suspense, useState, useEffect, useRef } from 'react'
import { Analytics } from '@vercel/analytics/react'
import App from './app'
import SpaceComm from './components/spacecomm/SpaceComm'

function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0)
  const [visible, setVisible] = useState(true)
  const [fadingOut, setFadingOut] = useState(false)
  const rafRef = useRef(null)
  const startTimeRef = useRef(Date.now())

  useEffect(() => {
    // Poll window.__sceneReady and simulate progress until the scene signals it's ready
    function tick() {
      const elapsed = Date.now() - startTimeRef.current
      const sceneReady = !!window.__sceneReady

      if (sceneReady) {
        // Scene has rendered its first frame — jump to 100% then fade out
        setProgress(100)
        setTimeout(() => {
          setFadingOut(true)
          setTimeout(() => {
            setVisible(false)
            onComplete()
          }, 1200)
        }, 400)
        return
      }

      // Simulate progress up to 90% while waiting for the scene
      const simulated = Math.min(90, (elapsed / 8000) * 90)
      setProgress(simulated)
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#00205B',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: fadingOut ? 0 : 1,
        transition: fadingOut ? 'opacity 1200ms ease' : 'none',
        backgroundImage: 'url(/textures/logos/SaxaVordLogo_whtText.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center calc(50% - 90px)',
        backgroundSize: '220px auto',
        pointerEvents: fadingOut ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          width: '280px',
          height: '4px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '2px',
        }}
      >
        <div
          style={{
            height: '4px',
            background: '#F68D2E',
            borderRadius: '2px',
            width: `${progress}%`,
            transition: 'width 200ms ease',
          }}
        />
      </div>
      <span
        style={{
          color: '#ffffff',
          fontSize: '10px',
          fontFamily: '"Pirulen Rg", sans-serif',
          letterSpacing: '0.1em',
          marginTop: '16px',
        }}
      >
        {`LOADING ${Math.floor(progress)}%`}
      </span>
    </div>
  )
}

// Create root for the CSS 'div' to reference //
const root = ReactDOM.createRoot(document.querySelector('#root'))

function Root() {
  const [loadingComplete, setLoadingComplete] = useState(false)

  return (
    <>
      {/* Wrapping the App in Suspense ensures the app is hidden until all elements
          within it are loaded - https://react.dev/reference/react/Suspense */}
      <Suspense fallback={null}>
        <App loadingComplete={loadingComplete} />
        <Analytics />
      </Suspense>

      {/* Custom loading screen — stays visible until the Canvas fires its first frame */}
      <LoadingScreen onComplete={() => setLoadingComplete(true)} />

      <SpaceComm
        iconSrc="/sc/DSL-Comms-Badge.svg"
        projects={[
          {
            screenshot: '/textures/sc/ba.png',
            title: 'Black Arrow',
            description: 'Tap the image to show the QR code and scan with your mobile device.',
            url: 'https://blackarrow.darkstarlabs.io/?sc=true'
          },
          {
            screenshot: '/textures/sc/Mu.png',
            title: 'Mün',
            description: 'Tap the image to show the QR code and scan with your mobile device.',
            url: 'https://mun.darkstarlabs.io/?sc=true'
          }
        ]}
      />
    </>
  )
}

root.render(<Root />)
